import { useEffect, useRef } from "react";
import { usePopStore } from "../state/usePopStore";
import { copy } from "../content/copy";
import { isNearMilestone, nextMilestone } from "../../shared/milestones";

// The Grand Tally as the front page's hero figure. Same discipline as
// PopCounterHud: transient store subscription + direct DOM writes, because the
// tally can move 20x/sec and must never re-render the hero tree.
//
// On the first server sync the number counts up from zero — the doors-opening
// moment — then live pops land directly. Reduced motion skips the count-up.
export default function HeroTally() {
  const numRef = useRef<HTMLSpanElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const liveRef = useRef<HTMLSpanElement>(null);
  const srRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let raf = 0;
    let entranceDone = false;
    let entranceRunning = false;
    let lastShown = 0;
    let lastPunch = 0;
    // Countdown mood: near a milestone the label alternates between its usual
    // caption and "N to 10,000 — push", swapped by a slow interval below.
    let showCountdown = false;
    const reduced =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      document.documentElement.dataset.reducedMotion === "true";

    const writeMood = (total: number) => {
      const next = nextMilestone(total);
      const near = isNearMilestone(total, next);
      numRef.current?.classList.toggle("is-near", near);
      const label = labelRef.current;
      if (!label) return;
      if (near && showCountdown && next !== null) {
        label.textContent = copy.hero.countdown
          .replace("{n}", (next - total).toLocaleString("en-US"))
          .replace("{next}", next.toLocaleString("en-US"));
        label.classList.add("is-countdown");
      } else {
        label.textContent = copy.hero.tallyLabel;
        label.classList.remove("is-countdown");
      }
    };

    const writeNow = () => {
      const { total, live } = usePopStore.getState();
      const num = numRef.current;
      writeMood(total);
      if (num) {
        num.textContent = total.toLocaleString("en-US");
        // Squash-and-stretch when the number grows — throttled so a 20/sec
        // flood reads as a wobble, not a strobe.
        const now = performance.now();
        if (!reduced && entranceDone && total > lastShown && now - lastPunch > 180) {
          lastPunch = now;
          num.classList.remove("squash");
          void num.offsetWidth; // reflow retriggers the animation
          num.classList.add("squash");
        }
        lastShown = total;
      }
      if (liveRef.current) {
        // Only claim presence once the socket has actually synced — before
        // that, "the hall is yours" would just be a guess.
        const { connection } = usePopStore.getState();
        const synced = connection === "open" || connection === "fallback";
        liveRef.current.textContent = !synced
          ? ""
          : live > 1
            ? copy.hero.liveMany.replace("{n}", String(live))
            : copy.hero.liveAlone;
      }
    };

    const countUp = (target: number) => {
      entranceRunning = true;
      const start = performance.now();
      const dur = Math.min(1800, 700 + Math.log10(Math.max(10, target)) * 260);
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 4);
        const value = Math.round(target * eased);
        if (numRef.current) numRef.current.textContent = value.toLocaleString("en-US");
        if (t < 1) {
          raf = requestAnimationFrame(tick);
        } else {
          entranceRunning = false;
          entranceDone = true;
          writeNow();
        }
      };
      raf = requestAnimationFrame(tick);
    };

    const onChange = () => {
      const { total } = usePopStore.getState();
      if (!entranceDone) {
        if (total <= 0 || entranceRunning) return;
        if (reduced) {
          entranceDone = true;
          writeNow();
        } else {
          countUp(total);
        }
        return;
      }
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        writeNow();
      });
    };

    writeNow();
    onChange();
    const unsub = usePopStore.subscribe(onChange);

    // Screen readers get one calm update per 10s, not one per pop.
    const srInterval = setInterval(() => {
      const { total } = usePopStore.getState();
      if (srRef.current && total > 0) {
        srRef.current.textContent = `${total.toLocaleString("en-US")} ${copy.hero.tallyLabel}`;
      }
    }, 10_000);

    // The label swap is a slow heartbeat, not an animation — it runs (and
    // shows the countdown) under reduced motion too; only the glow pulse is
    // motion, and CSS stills that separately.
    const moodInterval = setInterval(() => {
      showCountdown = !showCountdown;
      writeMood(usePopStore.getState().total);
    }, 4_000);

    return () => {
      unsub();
      if (raf) cancelAnimationFrame(raf);
      clearInterval(srInterval);
      clearInterval(moodInterval);
    };
  }, []);

  return (
    <div className="hero-tally">
      <span className="hero-tally-num" ref={numRef} aria-hidden="true">
        0
      </span>
      <span className="hero-tally-label" ref={labelRef} aria-hidden="true">
        {copy.hero.tallyLabel}
      </span>
      <span className="hero-tally-live" ref={liveRef} aria-hidden="true" />
      <span className="sr-only" aria-live="polite" ref={srRef} />
    </div>
  );
}
